import { FC, useEffect, useLayoutEffect, useState } from 'react';

import { LoadingOutlined, QuestionOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Dropdown, Form, Input, MenuProps, Modal, Spin } from 'antd';
import axios from 'axios';
import moment from 'moment/moment';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  formatPeriod,
  getAdGroupings,
  getFiltersWithoutSegment,
} from '../../../../../helpers/utils/functions';
import {
  handleReportPeriod,
  parseTableData,
} from '../../../../../helpers/utils/reports';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../../../../helpers/utils/ui';
import { unApi } from '../../../api/endpoints/reports/unApi';
import { tgApi } from '../../../api/endpoints/tgApi';
import { axiosConfigAuth } from '../../../api/middleware';
import { API_UN_REPORT_EXEL } from '../../../api/urls';
import { STATIC_LINK_DRAWER } from '../../../constants/staticLinkConstants';
import { Roles } from '../../../constants/user';
import { useActions } from '../../../hooks/useActions';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import { ReportMenu } from '../../nav/components/reportMenu/reportMenu';
import { listKeysGroupings } from '../GroupingsAD';

const HELP_LINKS = {
  1: 'https://help.scananalytics.ru/otchyot-po-registratsiyam/',
  2: 'https://help.scananalytics.ru/otchyot-po-zakazam/',
  3: 'https://help.scananalytics.ru/otchyot-po-platezham/',
  4: 'https://help.scananalytics.ru/otchyot-po-reklame/',
  5: 'https://help.scananalytics.ru/otchyot-po-vebinaram/',
};

const getReportName = (pathName: string, name?: string) => {
  switch (pathName) {
    case '/universal':
      return name? `Отчёт по регистрациям: ${name}`: 'Новый отчёт по регистрациям';
    case '/order-reports':
      return name?  `Отчёт по заказам: ${name}` : 'Новый отчёт по заказам';
    case '/payment-reports':
      return name?  `Отчёт по платежам: ${name}` : 'Новый отчёт по платежам';
    case '/ad-reports':
      return name?  `Отчёт по рекламе: ${name}` : 'Новый отчёт по рекламе';
    case '/webinar-reports':
      return name?  `Отчёт по вебинарам: ${name}` : 'Новый отчёт по вебинарам';
    default:
      return name?  `Отчёт: ${name}` : 'Новый отчёт';
  }
};

const UniversalReportTitle: FC = () => {
  const location = useLocation();
  const [ searchParams, setSearchParams ] = useSearchParams();
  const { setAppReportOpen, setDrawerLink } = useActions();
  const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
  const {
    savedReports: reports,
    currentReport: storeReport,
    isLoadingReports,
  } = useTypedSelector((state) => state.reports);
  const period = useTypedSelector((state) => state.period);
  const { details } = useTypedSelector((state) => state.details);
  const { filters, currentSegment } = useTypedSelector((state) => state.filter);
  const { attributes } = useTypedSelector((state) => state.attributes);
  const { chartType } = useTypedSelector((state) => state.chart);
  const {
    metrics,
    groupings,
    groupsChecked,
    metricsChecked,
    filtersTable,
    sortTable,
  } = useTypedSelector((state) => state.table);
  const { isCompare } = useTypedSelector((state) => state.meta);

  const {
    setFilters,
    setDetails,
    setChartType,
    setGroupings,
    setMetrics,
    setFiltersTable,
    setSortTable,
    setPeriod,
    setCurrentReport,
    setMetricsFiltersUpdate,
    setCurrentSegment,
    setAttributes,
    
    postReport,
    putReport,
    setMetaTableFetching,
    setComparePeriod,
    setAllDataFetching,
  } = useActions();
  const [ isModalVisible, setIsModalVisible ] = useState(false);
  const [ isModalCreateVisible, setIsModalCreateVisible ] = useState(false);
  const [ isModalXLSXVisible, setIsModalXLSXVisible ] = useState(false);
  const [ reportName, setReportName ] = useState('');
  const [ excelName, setExcelName ] = useState('');
  const [ isDisableSave, setIsDisableSave ] = useState(false);
  const [ isReportLoading, setIsReportLoading ] = useState(false);
  const { savedReports } = useTypedSelector((state) => state.reports);
  const { role } = useTypedSelector((state) => state.currentUser);

  const navigate = useNavigate();
  const currentReport = savedReports.find((el: any) => el.id === storeReport.id)
    ? savedReports.find((el: any) => el.id === storeReport.id)
    : { ...storeReport, is_favorite: false, name: 'Без Имени' };

  useEffect(() => {
    const reportHash = searchParams.get('report_id');
    if (savedReports.find((report) => report?.hash === reportHash)?.access_id <= Roles[role]) {
      setIsDisableSave(false);
    } else {
      setIsDisableSave(true);
    }
  }, [ searchParams, savedReports ]);

  useLayoutEffect(() => {
    const reportHash = searchParams.get('report_id'); // Получаем хэш из состояния
    if (reportHash) {
      setReportFromUrl(reportHash); // Загружаем отчет, если хэш есть
    }
  }, [ searchParams ]);


  const handleOpenHelpFrame = () => {
    const link = HELP_LINKS[currentReport.report_id as keyof typeof HELP_LINKS] ?? STATIC_LINK_DRAWER.reports;
    setDrawerLink(link);
    setAppReportOpen(true);
  };

  const save = () => {
    const current_un_period_selection = period.select;
    let values: any = {
      ...currentReport.values,
      period: {
        start: moment(period.start).format('YYYY-MM-DD'),
        end: moment(period.end).format('YYYY-MM-DD'),
        interval: period.interval || 'day',
      },
      details,
      chartType,
      groupings,
      metrics,
      groupsChecked,
      metricsChecked,
      filtersTable,
      sortTable,
      attributes,
      current_un_period_selection: current_un_period_selection
        ? current_un_period_selection
        : '',
      segment: null,
      filtersReq: attributes,
    };

    if (Object.keys(currentSegment || {}).length > 0) {
      values.segment = currentSegment;
      values.filters = getFiltersWithoutSegment(
        filters,
        currentSegment?.values?.filters
      );
    } else {
      values.segment = {};
      values.filters = filters;
      values.attributes = attributes;
    }

    if (selectedSchool?.id) {
      if (period.interval === 'ndaysago' || period.select === 'ndaysago') {
        values.period.days = period.days;
      }
      putReport(selectedSchool.id, { id: currentReport.id, values });
    }
  };

  const saveAs = () => {
    if (selectedSchool?.id) {
      const current_un_period_selection = period.select;
      let values: any = {
        name: reportName,
        period: {
          start: moment(period.start).format('YYYY-MM-DD'),
          end: moment(period.end).format('YYYY-MM-DD'),
          interval: period.interval || period.select,
          select: period.select || period.interval,
        },
        details,
        chartType,
        groupings,
        metrics,
        groupsChecked,
        metricsChecked,
        filtersTable,
        sortTable,
        attributes,
        filtersReq: attributes,
        current_un_period_selection: current_un_period_selection
          ? current_un_period_selection
          : '',
        segment: null,
      };

      if (Object.keys(currentSegment || {}).length > 0) {
        values.segment = currentSegment;
        values.filters = getFiltersWithoutSegment(
          filters,
          currentSegment?.values?.filters
        );
      } else {
        let _filters = filters;
        if (!Array.isArray(filters)) {
          _filters = [];
        }
        values.filters = _filters;
        values.attributes = attributes;
      }
      if (period.interval === 'ndaysago' || period.select === 'ndaysago') {
        values.period.days = period.days;
      }
      const onSuccess = (report_id: number) => navigate(window.location.pathname + '?report_id=' + report_id);
      window.history.replaceState({}, document.title, window.location.pathname);
      postReport(selectedSchool.id, { values, onSuccess });
      setIsModalVisible(false);
      setReportName('');
    }
  };

  const create = () => {
    if (selectedSchool?.id) {
      const current_un_period_selection = period.select;
      let values: any = {
        name: reportName,
        period: {
          start: moment(period.start).format('YYYY-MM-DD'),
          end: moment(period.end).format('YYYY-MM-DD'),
          interval: period.interval || period.select,
          select: period.select || period.interval,
        },
        details,
        chartType,
        groupings,
        metrics,
        groupsChecked,
        metricsChecked,
        filtersTable,
        sortTable,
        filtersReq: attributes,
        current_un_period_selection: current_un_period_selection
          ? current_un_period_selection
          : '',
        segment: null,
      };

      if (Object.keys(currentSegment || {}).length > 0) {
        values.segment = currentSegment;
        values.filters = getFiltersWithoutSegment(
          filters,
          currentSegment?.values?.filters
        );
      } else {
        let _filters = filters;
        if (!Array.isArray(filters)) {
          _filters = [];
        }
        values.filters = _filters;
        values.attributes = attributes;
      }
      if (period.interval === 'ndaysago' || period.select === 'ndaysago') {
        values.period.days = period.days;
      }
      const reportData = {
        access_id: 1,
        values,
      };
      postReport(selectedSchool.id, reportData);
      setIsModalCreateVisible(false);
      setReportName('');
    }
  };

  const manualActivateReport = (report: any) => {
    const metrics = parseTableData(report.values.metrics);
    let filters = report.values.filters;

    if (!Array.isArray(report.values.filters)) {
      filters = [];
    }

    // const handledPeriod = handleReportPeriod(report.values.period)
    const reportPeriod = report.values?.period || {};
    const handledPeriod = handleReportPeriod(reportPeriod);

    setCurrentReport(report);
    setPeriod({
      start: handledPeriod.start,
      end: handledPeriod.end,
      interval: handledPeriod.interval,
      select: '',
    });
    setComparePeriod({
      index: 2,
      period: {
        start: handledPeriod.start,
        end: handledPeriod.end,
        interval: handledPeriod.interval,
      },
    });
    setDetails(report.values.details);
    setChartType(report.values.chartType);
    location.pathname.includes('ad-reports')
      ? setGroupings({
          data: getAdGroupings(report.values.groupings, listKeysGroupings),
        })
      : setGroupings({ data: report.values.groupings });
    setMetrics({ data: metrics });
    setFiltersTable(report.values.filtersTable);
    setSortTable(report.values.sortTable ?? {});
    setAttributes(report.values.filtersReq);

    setTimeout(() => {
      setMetaTableFetching({ value: true });
      setMetricsFiltersUpdate(true);
      setAllDataFetching({ value: true });
    }, 200);

    const { segment } = report.values;
    const isSegment = segment && Object.keys(report.values.segment).length > 0;

    if (isSegment) {
      setCurrentSegment(segment);
      setFilters([ ...segment.values.filters, ...filters ]);
    } else {
      setCurrentSegment(null);
      setFilters(filters);
    }

    setAllDataFetching({ value: true });
    setTimeout(() => {
      setMetaTableFetching({ value: true });
      setMetricsFiltersUpdate(true);
    }, 15);
  };

  const saveXSLX = () => {
    const format = 'YYYY-MM-DD';
    const _period = {
      ...formatPeriod({ start: period.start, end: period.end }, format),
    };
    const data = {
      name: excelName,
      period: _period,
      filters: attributes,
      groupings,
      metrics,
    };

    axios
      .post(API_UN_REPORT_EXEL, data, axiosConfigAuth())
      .then(() => {
        showSuccessMessage('Сохранено');
      })
      .catch(() => {
        showErrorMessage('Ошибка при сохранении');
      });

    setIsModalXLSXVisible(false);
    setExcelName('');
  };

  const setReportFromUrl = async (reportHash: string) => {
    setIsReportLoading(true); // Активируем загрузку
    try {
      const response = await unApi.getReportDataFromHash(reportHash);
      if (response.status === 200 && response.data[0]) {
        manualActivateReport(response.data[0]);
        //manualActivateReport(currentReport);
      }
    } catch (e) {
      showErrorMessage('Ошибка загрузки отчёта');
    } finally {
      setIsReportLoading(false); // Выключаем загрузку
    }
  };

  useEffect(() => {
    if (!location.pathname.includes('/webinar/quality')) {
      const reportsId: number[] = [];
      reports.forEach((report: { id: number }) => {
        (async () => {
          const isExist = await tgApi.isExistTask(report.id);
          if (isExist) {
            reportsId.push(report.id);
          }
        })();
      });
    }
  }, [ reports ]);

  const savingItemsDropDown: MenuProps['items'] = [
    {
      key: '1',
      label: Object.keys(currentReport).length > 0 && !isDisableSave && (
        <span onClick={save}>Сохранить изменения</span>
      ),
    },
    {
      key: '2',
      label: <span onClick={() => setIsModalVisible(true)}>Сохранить как новый отчет</span>,
    },
  ];

  return (
      <>
        <div style={{ display: 'flex' }}>
          <div style={{ 
                display: 'flex', 
                alignItems: 'baseline',
                fontSize: '20px',
                fontWeight: 'bold' 
          }}>
            <div>
              {isReportLoading ? (
                <Spin indicator={<LoadingOutlined />} />
              ) : 
                getReportName(location.pathname.replace('users/', ''), currentReport?.values?.name)}
            </div>
            {currentReport.id && (
              <ReportMenu isDashboard={false} item={currentReport} trigger='click'/>
            )}
            <button
              onClick={handleOpenHelpFrame}
              style={{
                border: 'none',
                outline: 'none',
                minWidth: 20,
                gap: 10, 
                background: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                color: 'red',
                fontSize: 14,
                cursor: 'pointer'
              }}
            >
              <QuestionOutlined size={10}  style={{ background: 'red', color: 'white', borderRadius: '50%', padding: 5, fontSize: 12 }}/>
              <span>Как работать с отчетом {getReportName(location.pathname.replace('users/', '')).replace('Новый отчёт', '').replace(':', '?')}</span>
            </button>
          </div>
          {!isCompare && (
            <div style={{ marginLeft: 'auto' }}>
              <Dropdown
                overlayStyle={{ left: 1490 }}
                disabled={isCompare}
                menu={{ items: savingItemsDropDown }}
                trigger={[ 'click' ]}
              >
                <Button  disabled={isCompare}
                style={{
                  height: '45px',
                  marginRight: '10px',
                  borderRadius: '5px',
                }} >
                  Сохранить
                </Button>
              </Dropdown>
            </div>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 0,
            width: '100%',
          }}
        >
          <Modal
            title="Сохранить отчет как"
            open={isModalVisible}
            onOk={() =>
              reportName
                ? saveAs()
                : showErrorMessage('Введите название отчета')
            }
            onCancel={() => setIsModalVisible(false)}
            okText="Сохранить"
          >
            <Form>
              <Form.Item
                name="reportName"
                label="Название отчета"
                rules={[
                  { required: true, message: 'Введите название отчета' },
                  { min: 1, message: 'Введите название отчета' },
                ]}
              >
                <Input
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                  size="large"
                  placeholder="Введите название отчета"
                  allowClear
                />
              </Form.Item>
            </Form>
          </Modal>
          <Modal
            title="Скачать XLSX таблицы"
            open={isModalXLSXVisible}
            onOk={saveXSLX}
            onCancel={() => setIsModalXLSXVisible(false)}
            okText="Сохранить"
          >
            <Input
              value={excelName}
              onChange={(e) => setExcelName(e.target.value)}
              size="large"
              placeholder="Введите название файла"
              allowClear
            />
          </Modal>
        </div>
      </>
  );
};

export default UniversalReportTitle;
